export function estimatePagesFromChars(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return null;
  const rawPages = numericValue / 1500;
  if (rawPages < 30) return Math.max(5, Math.round(rawPages / 5) * 5);
  return Math.round(rawPages / 10) * 10;
}

// The signup ask belongs to the server's `sign_up_required`, not to "is this visitor
// anonymous". An anonymous visitor gets several free quizzes, so finishing one is not
// being locked out — prompting on every completion showed a first-timer "you've used up
// your free quizzes" while they still had two, over the top of their own results.
export function shouldPromptSignup({ isAnonymous, signUpRequired }) {
  return Boolean(isAnonymous) && Boolean(signUpRequired);
}

// Gated routes replace themselves with /sign-up, so pressing Back from /sign-up lands on
// a gated route that sends the visitor straight back: they could never leave. A Back or
// Forward press goes home instead. `popstate` fires only for those, never on a first load
// or a reload, which react-router also reports as POP.
let sawPopstate = false;
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    sawPopstate = true;
  });
}

export function signupWallTarget(navigationType) {
  return navigationType === "POP" && sawPopstate ? "/" : "/sign-up";
}

// Sample and topic books are owned by the system, so they never show up in the
// user's library — their progress would otherwise be invisible on the dashboard.
// An anonymous visitor gets this many mock questions before the sign-up wall; enough
// to be invested, and the score only comes at the end.
export const MOCK_FREE_QUESTIONS = 20;

export function mockSignupWallHit({ isMock, isAnonymous, quizComplete, currentIndex }) {
  return Boolean(isMock && isAnonymous && !quizComplete && currentIndex >= MOCK_FREE_QUESTIONS);
}

export function continueLearningItems(progress, books) {
  const owned = new Set((books || []).map((book) => book.id));
  return (progress || []).filter((item) => item.total_attempts > 0 && !owned.has(item.book_id));
}

// A signed-in learner who used every free round today otherwise lands on a dashboard
// with nothing to do. Bonus quizzes still count as rounds left; Pro has no cap.
export function isOutOfRounds(limits) {
  return Boolean(
    limits &&
    !limits.is_anonymous &&
    limits.tier !== "pro" &&
    limits.max_daily_quizzes > 0 &&
    limits.remaining_daily_quizzes === 0 &&
    !(limits.bonus_quizzes_balance > 0),
  );
}

// Rounds a learner can still start today. Anonymous visitors have a lifetime cap
// (max_total_quizzes) as well as the daily one, and the lifetime cap is usually lower:
// showing only the daily number told a visitor with 2 rounds left they had 4.
export function quizRoundsLeft(limits) {
  const daily = limits?.remaining_daily_quizzes;
  const total = limits?.max_total_quizzes;
  const used = limits?.quizzes_completed;
  if (typeof total !== "number" || total < 0 || typeof used !== "number") {
    return { count: daily, dailyCapApplies: true };
  }
  const totalLeft = Math.max(total - used, 0);
  return typeof daily === "number" && daily <= totalLeft
    ? { count: daily, dailyCapApplies: true }
    : { count: totalLeft, dailyCapApplies: false };
}
